import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowUpRight, ExternalLink } from 'lucide-react';
import { useReveal } from '../../hooks/useReveal';
import { CASE_STUDIES, FEATURED_SLUGS } from '../../lib/caseStudyData';

const ease = [0.22, 1, 0.36, 1];

export function FeaturedSystems() {
    const ref = useReveal();
    const systems = FEATURED_SLUGS
        .map((slug) => CASE_STUDIES[slug])
        .filter(Boolean);

    return (
        <section
            id="featured-systems"
            data-testid="featured-systems-section"
            className="relative section overflow-hidden"
        >
            <div className="atmosphere opacity-50" />

            <div className="shell relative">
                <div ref={ref} className="reveal flex flex-col lg:flex-row lg:items-end justify-between gap-8">
                    <div className="max-w-2xl">
                        <p className="eyebrow">Featured systems · Real builds, shipped</p>
                        <h2 className="mt-6 font-display font-semibold text-[2.6rem] sm:text-[3.6rem] lg:text-[4.6rem] leading-[1.02] tracking-[-0.04em] text-white">
                            Systems we have{' '}
                            <span className="text-gradient-cyan">put into the world.</span>
                        </h2>
                    </div>
                    <p className="text-[15.5px] leading-[1.6] text-slate-400 max-w-sm">
                        Each project below is a live product — designed, built
                        and engineered end-to-end by ClearAdvance.
                    </p>
                </div>

                <div className="mt-14 lg:mt-20 grid grid-cols-1 md:grid-cols-2 gap-5 lg:gap-7">
                    {systems.map((s, i) => (
                        <CaseCard key={s.slug} {...s} idx={i} />
                    ))}
                </div>

                <div className="mt-12 text-center">
                    <Link to="/portfolio/zubite-platform" className="btn-link" data-testid="all-cases-link">
                        Browse the full case files
                        <ArrowUpRight className="w-3.5 h-3.5" />
                    </Link>
                </div>
            </div>
        </section>
    );
}

function CaseCard({ slug, fileNo, kind, region, year, title, oneLiner, image, meta, idx }) {
    const reveal = useReveal();
    return (
        <motion.article
            ref={reveal}
            data-testid={`featured-system-${slug}`}
            className="reveal group relative"
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.8, delay: (idx % 2) * 0.1, ease }}
        >
            <Link
                to={`/portfolio/${slug}`}
                className="block relative card-solid h-full"
            >
                {/* Image */}
                <div className="relative aspect-[4/2.7] overflow-hidden">
                    <div
                        className="absolute inset-0 transition-transform duration-700 ease-out group-hover:scale-[1.04]"
                        style={{
                            backgroundImage: `url(${image})`,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center top',
                        }}
                    />
                    <div
                        className="absolute inset-0 pointer-events-none"
                        style={{ background: 'linear-gradient(180deg, rgba(5,10,21,0) 35%, rgba(5,10,21,0.78) 100%)' }}
                    />
                    {/* Top-left file no */}
                    <div className="absolute top-4 left-4 px-2.5 py-1 rounded-md font-mono text-[10px] tracking-[0.2em] uppercase font-medium text-cyan-300 backdrop-blur-md"
                        style={{ background: 'rgba(5,10,21,0.55)', border: '1px solid rgba(6,182,212,0.30)' }}>
                        FILE {fileNo}
                    </div>
                    {/* Top-right hover arrow */}
                    <div
                        className="absolute top-4 right-4 grid place-items-center w-9 h-9 rounded-full backdrop-blur-md transition-all duration-500 group-hover:bg-cyan-500/90 group-hover:translate-x-1 group-hover:-translate-y-1"
                        style={{ background: 'rgba(255,255,255,0.10)', border: '1px solid rgba(255,255,255,0.18)' }}
                    >
                        <ArrowUpRight className="w-4 h-4 text-white" />
                    </div>
                </div>

                {/* Content */}
                <div className="p-6 lg:p-7">
                    <div className="flex items-center gap-2 font-mono text-[10px] tracking-[0.18em] uppercase text-slate-500">
                        <span>{kind}</span>
                        <span className="text-slate-700">·</span>
                        <span>{region}</span>
                        <span className="text-slate-700">·</span>
                        <span>{year}</span>
                    </div>
                    <h3 className="mt-4 font-display font-semibold text-[1.5rem] lg:text-[1.7rem] tracking-[-0.025em] text-white leading-[1.15]">
                        {title}
                    </h3>
                    <p className="mt-3 text-[14.5px] leading-[1.6] text-slate-400 line-clamp-2">
                        {oneLiner}
                    </p>

                    {meta && meta.length > 0 && (
                        <div className="mt-6 flex flex-wrap gap-2">
                            {meta.slice(0, 3).map((m) => (
                                <span
                                    key={m}
                                    className="px-2.5 py-1 rounded-md text-[11px] font-medium font-mono tracking-[0.06em] text-slate-300"
                                    style={{
                                        background: 'rgba(255,255,255,0.04)',
                                        border: '1px solid rgba(255,255,255,0.08)',
                                    }}
                                >
                                    {m}
                                </span>
                            ))}
                        </div>
                    )}

                    <div className="mt-7 pt-5 border-t border-white/[0.06] flex items-center gap-2 text-cyan-400 text-[13px] font-medium">
                        <span>Open case file</span>
                        <ExternalLink className="w-3 h-3" />
                    </div>
                </div>

                {/* Glow border on hover */}
                <div
                    className="pointer-events-none absolute inset-0 rounded-[1.25rem] opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                    style={{
                        boxShadow: '0 0 0 1px rgba(6,182,212,0.45), 0 30px 60px -28px rgba(6,182,212,0.4)',
                    }}
                />
            </Link>
        </motion.article>
    );
}
